// App store: session, server AppState, live connection, toast, chat stream. Actions talk to the API and apply the
// returned `state`; card actions are optimistic (instant UI, rolled back with a toast if the server refuses).
//
//   const plan = useApp((s) => s.state?.plan)          subscribe to a slice (re-renders on shallow change)
//   await doneCard(id)                                  -> Reward | null (screen then calls go('R', { cur: id }))
//   toast('Removed', { undo: () => restoreCard(id) })
import type { AppState, Card, CardEdit, CharId, ChatEvent, DoneFrom, DoneNoteData, LaterWhen, Message, Reward, StuckAction, StuckChatData, StuckHelp, StuckReason, RegisterRequest } from '@shared/types';
import { C } from '@shared/content/characters';
import { STUCK_REASON_LABEL, fill } from '@shared/content/lines';
import { STUCK_CHAT, STUCK_SAID, stuckAlt, stuckOutLater, stuckOutStart } from '@shared/content/stuck';
import { laterTarget, fmtTime, levelOf, localMinutes, parseTime } from '@shared/rules';
import { createStore, useStore } from './lib/createStore';
import { api, ApiError, onUnauthorized } from './api/client';
import type { AudioPayload, ChatRequest, DebugBody, DebugTool, FriendAction, ImagePayload, NotifyAction, SettingsPatch } from './api/types';
import { LIVE_CLOSE } from './api/types';
import { getToken, setToken } from './api/token';
import { connectLive, type LiveConnection, type LiveStatus } from './api/live';
import { currentPushMode, desktop, isDesktopShell, isMiniWindow, isNativeMobile, onNative, registerPush, scheduleLocal } from './native';
import { STOPPED_EVENT, STOPPED_KEY, deskSession } from './lib/deskSession';
import { routerStore } from './router';

export interface ToastState {
  id: number;
  text: string;
  /** Shows an "Undo" text button. */
  undo?: (() => void) | null;
  /** Or a custom action button (e.g. { label: 'See today', run }). */
  action?: { label: string; run: () => void } | null;
}

export interface ChatStream {
  sending: boolean;
  /** Messages received during the current send (user echo first), until 'end' brings the full state. */
  pending: Message[];
  /** Streaming text of the buddy reply ('delta' events). */
  text: string;
  /** Set by 'limit' / 'crisis' events; the Chat screen reacts (M4 / M6) and clears them with clearChatFlags(). */
  limit: boolean;
  crisis: boolean;
  /** A card was completed from chat (tool complete_card): the Chat screen opens R for this card id. */
  rewardFor: string | null;
  error: string | null;
}

export interface AppStore {
  booted: boolean;
  token: string | null;
  state: AppState | null;
  live: LiveStatus;
  toast: ToastState | null;
  chat: ChatStream;
}

const emptyChat: ChatStream = { sending: false, pending: [], text: '', limit: false, crisis: false, rewardFor: null, error: null };
export const appStore = createStore<AppStore>({ booted: false, token: getToken(), state: null, live: 'off', toast: null, chat: emptyChat });

// ------------------------------------------------------------------ hooks
export function useApp<T>(select: (s: AppStore) => T): T { return useStore(appStore, select); }
/** The server state (null before sign-in). */
export const useAppState = () => useApp((s) => s.state);
/** Current buddy id (signed-in state, else 'margo'). Onboarding screens use the draft's ch instead. */
export const useCurrentCh = (): CharId => useApp((s) => s.state?.ch || 'margo');

// ------------------------------------------------------------------ state
const clone = <T>(x: T): T => (typeof structuredClone === 'function' ? structuredClone(x) : JSON.parse(JSON.stringify(x)));

/** Apply a server state unless it is older than what we have (v is monotonic per user). */
export function applyState(next: AppState | null | undefined) {
  if (!next) return;
  const cur = appStore.get().state;
  if (cur && next.v < cur.v && cur.user.id === next.user.id) return;
  appStore.set({ state: next });
  afterState(next);
}

export function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.message || "Can't reach Xiaoman. Check your connection.";
  return 'Something went wrong. Try again.';
}

// ------------------------------------------------------------------ toast
let toastSeq = 0;
let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function toast(text: string, opts: { undo?: () => void; action?: { label: string; run: () => void }; ms?: number } = {}) {
  if (toastTimer) clearTimeout(toastTimer);
  const id = ++toastSeq;
  appStore.set({ toast: { id, text, undo: opts.undo || null, action: opts.action || null } });
  toastTimer = setTimeout(() => { if (appStore.get().toast?.id === id) appStore.set({ toast: null }); }, opts.ms ?? (opts.undo || opts.action ? 4000 : 2800));
}
export function dismissToast() { if (toastTimer) clearTimeout(toastTimer); appStore.set({ toast: null }); }
// A toast belongs to the screen it was shown on (F_Phone clears it on every navigation): a new screen clears it, so an
// Undo never floats over R or follows to Today. A flow that wants a toast on the new screen shows it after navigating
// (go() is synchronous) or hands it over in the nav params (`toast`, shown on arrival).
let toastScreen = routerStore.get().s;
routerStore.subscribe((r) => {
  if (r.s === toastScreen) return;
  toastScreen = r.s;
  if (appStore.get().toast) dismissToast();
});

// ------------------------------------------------------------------ session
let live: LiveConnection | null = null;

export async function refresh(): Promise<AppState | null> {
  if (!appStore.get().token) return null;
  try { const r = await api.state(); applyState(r.state); return r.state; } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 401) console.warn('refresh failed', e);
    return null;
  }
}

function startLive() {
  live?.close();
  live = connectLive({
    token: () => appStore.get().token,
    onStatus: (s) => appStore.set({ live: s }),
    onReconnect: () => { void refresh(); },
    onAuthClosed: (code) => {
      void signOut({ remote: false });
      toast(code === LIVE_CLOSE.deleted ? 'This account no longer exists.' : 'You were signed out. Please sign in again.');
    },
    onMessage: (m) => {
      if (m.t === 'state') applyState(m.state);
      else if (m.t === 'notify') {
        if (isMiniWindow()) return; // the main window shows notifications and acks them; the small window only needs states
        const n = m.note;
        const route = (n.data && typeof n.data.route === 'string' && n.data.route) || (n.kind === 'card' ? 'T' : 'C');
        if (isDesktopShell()) desktop.notify({ id: n.id, title: n.title, body: n.body, actions: n.actions, route });
        else toast(`${n.title}: ${n.body}`);
        live?.send({ t: 'seen', noteId: n.id });
      }
    },
  });
}

async function startSession(token: string, state: AppState) {
  setToken(token);
  appStore.set({ token, state: null });
  applyState(state);
  startLive();
}

export async function signIn(email: string, password: string): Promise<AppState> {
  const r = await api.login(email.trim(), password);
  await startSession(r.token, r.state);
  if (r.state.user.reminders) void enableReminders();
  return r.state;
}

export async function register(req: RegisterRequest): Promise<AppState> {
  const r = await api.register(req);
  await startSession(r.token, r.state);
  return r.state;
}

export async function signOut(opts: { remote?: boolean } = { remote: true }) {
  if (opts.remote !== false && appStore.get().token) { try { await api.logout(); } catch { /* token dies anyway */ } }
  live?.close(); live = null;
  setToken(null);
  appStore.set({ token: null, state: null, chat: emptyChat, live: 'off' });
  if (desktop.available() && !isMiniWindow()) desktop.setTray(null); // 0.2.0 shells: her face and the small window go
}

/** Calls that end this session: close our own socket first (its 4001/4002 close would toast), then sign out. */
async function endSession(call: () => Promise<unknown>): Promise<boolean> {
  live?.close(); live = null;
  try { await call(); } catch (e) { toast(errorText(e)); if (appStore.get().token) startLive(); return false; }
  await signOut({ remote: false });
  return true;
}
/** Sign out on every device (M1). Other devices' sockets close with 4001 and they sign out too. */
export const signOutEverywhere = () => endSession(() => api.logoutAll());
/** Delete the account (M1). The shell guard then shows O1. */
export const deleteAccount = () => endSession(() => api.deleteAccount());
/** Test tool: delete the account and refund the invite use, so onboarding can be run again. */
export const restartAccount = () => endSession(() => api.debugRestart());

/** Ask for notification permission and register this device (push, or local notifications as the fallback). */
export async function enableReminders(): Promise<void> {
  if (!isNativeMobile()) return;
  const mode = await registerPush();
  if (mode === 'local') void syncLocalNotes(true);
}

// Local notification fallback: reschedule on every state change (debounced).
let localTimer: ReturnType<typeof setTimeout> | null = null;
async function syncLocalNotes(now = false) {
  if (currentPushMode() !== 'local' || !appStore.get().token) return;
  if (localTimer) clearTimeout(localTimer);
  localTimer = setTimeout(async () => {
    try { const r = await api.schedule(); await scheduleLocal(r.items); } catch (e) { console.warn('schedule failed', e); }
  }, now ? 0 : 2000);
}

function afterState(st: AppState) {
  sendTray(st);
  void syncLocalNotes();
}

/** The menu bar (desktop shells): today, the next card and the Working-together timer. Sent on every state, and when a
 *  session is stopped or started again in the room or the small window (lib/deskSession: localStorage, not state). */
function sendTray(st: AppState) {
  if (desktop.available() && !isMiniWindow()) {
    const cards = st.plan.cards.filter((c) => c.st !== 'removed');
    const next = cards.find((c) => c.st === 'todo') || null;
    // The card being worked on, for the menu bar timer. startedAt in this device's clock (the server's can run ahead).
    const work = deskSession(st);
    const skew = Date.parse(st.now) - Date.now();
    desktop.setTray({
      ch: st.ch,
      title: `${C[st.ch].name} · ${st.streak.count}-day streak`,
      today: `Today · ${cards.filter((c) => c.st === 'done').length} of ${cards.length} done`,
      next: next ? { time: next.time, title: next.title } : null,
      quietUntil: null,
      work: work && work.startedAt ? { title: work.alt || work.title, startedAt: new Date(Date.parse(work.startedAt) - (Number.isFinite(skew) ? skew : 0)).toISOString() } : null,
    });
  }
}

/** Called once by the app shell. */
export function initSession() {
  // The ✕ on the room's timer card (or Start again in the small window) changes no state: re-send the menu bar.
  const trayAgain = () => { const { token, state } = appStore.get(); if (token && state) sendTray(state); };
  if (typeof window !== 'undefined') {
    window.addEventListener(STOPPED_EVENT, trayAgain);
    window.addEventListener('storage', (e) => { if (e.key === STOPPED_KEY || e.key == null) trayAgain(); });
  }
  onUnauthorized(() => {
    if (!appStore.get().token) return;
    void signOut({ remote: false });
    toast('Please sign in again.');
  });
  onNative((e) => {
    if (e.type === 'resume') { void refresh(); live?.kick(); }
    if (e.type === 'push-token') void api.devices({ platform: e.platform, token: e.token }).catch((err) => console.warn(err));
    // Desktop notification buttons (morning "In 30 minutes", follow-up "Not today" / "Accept"). The click also routes.
    // Phone notification buttons ('note-action', native.ts) work the same way.
    if ((e.type === 'desktop-note-action' || e.type === 'note-action') && (e.action === 'snooze' || e.action === 'not-today' || e.action === 'accept') && appStore.get().token) {
      void notifyAction(e.id, e.action);
    }
  });
}

/** Boot: restore the session from the stored token. Returns the state, or null when signed out / offline. */
export async function bootSession(): Promise<AppState | null> {
  const token = getToken();
  if (!token) { appStore.set({ booted: true }); return null; }
  appStore.set({ token });
  try {
    const r = await api.state();
    applyState(r.state);
    startLive();
    appStore.set({ booted: true });
    // Register this phone again on every launch: Apple recommends it (device tokens can change), and a phone that
    // updated from an FCM build to a direct-APNs build keeps its session, so it would never register otherwise.
    // No prompt when permission was already answered.
    if (r.state.user.reminders) void enableReminders();
    return r.state;
  } catch (e) {
    appStore.set({ booted: true });
    if (e instanceof ApiError && e.status === 401) { await signOut({ remote: false }); return null; }
    toast(errorText(e), { action: { label: 'Retry', run: () => { void bootSession(); } } });
    startLive();
    return null;
  }
}

// ------------------------------------------------------------------ optimistic helper
async function optimistic<T extends { state: AppState }>(mutate: ((s: AppState) => void) | null, call: () => Promise<T>): Promise<T | null> {
  const before = appStore.get().state;
  let mine: AppState | null = null;
  if (before && mutate) {
    mine = clone(before);
    mutate(mine);
    appStore.set({ state: mine });
  }
  try {
    const res = await call();
    applyState(res.state);
    return res;
  } catch (e) {
    if (mine && appStore.get().state === mine) appStore.set({ state: before });
    else void refresh();
    toast(errorText(e));
    return null;
  }
}
const card = (s: AppState, id: string): Card | undefined => s.plan.cards.find((c) => c.id === id);
const nowIso = () => new Date().toISOString();

// ------------------------------------------------------------------ plan + cards (optimistic where it is safe)
export const confirmPlan = (messageId: string) => optimistic((s) => {
  s.plan.status = 'planned';
  const m = s.messages.find((x) => x.id === messageId);
  if (m && m.data) m.data = { ...m.data, status: 'accepted' };
}, () => api.planConfirm(messageId));

/** Fix the rows of a plan card before "Looks good" (a misheard word, a wrong time). */
export const editPlan = (messageId: string, cards: { time: string; title: string; min: number }[]) => optimistic((s) => {
  const m = s.messages.find((x) => x.id === messageId);
  if (m && m.data) m.data = { ...m.data, cards: cards.map((c) => ({ ...c, at: parseTime(c.time) ?? 0 })).sort((a, b) => a.at - b.at) };
}, () => api.planEdit(messageId, cards));

/** Change a card still to do (title, time, minutes). */
export const editCard = (id: string, edit: CardEdit) => optimistic((s) => {
  const c = card(s, id); if (!c) return;
  if (edit.title != null) c.title = edit.title;
  if (edit.min != null) c.min = edit.min;
  const at = edit.time != null ? parseTime(edit.time) : null;
  if (at != null) { c.at = at; c.time = fmtTime(at); s.plan.cards.sort((a, b) => a.at - b.at || a.order - b.order); }
}, () => api.cardEdit(id, edit));

export const startCard = (id: string, alt: string | null = null) => optimistic((s) => {
  const c = card(s, id); if (c) { c.startedAt = c.startedAt || nowIso(); c.alt = alt; }
}, () => api.cardStart(id, alt));

let lastReward: Reward | null = null; // the last reward doneCard got (Undo from Today has no note to read the points from)
/** Finish a card. Resolves with the Reward (also in state.reward) or null on failure. `from` (0.2.0): 'chat' = ticked
 *  in chat, 'work-chat' = Working together started from chat: the server adds the 'done' note (tick line + her line)
 *  to the chat; none from Today. isBigReward(reward) -> full-screen R; otherwise the light celebration (then
 *  rewardSeen(), and Undo = undoneCard(id)). */
export async function doneCard(id: string, opts: { from?: DoneFrom } = {}): Promise<Reward | null> {
  const res = await optimistic((s) => { const c = card(s, id); if (c) { c.st = 'done'; c.doneAt = nowIso(); } }, () => api.cardDone(id, opts.from));
  if (res) lastReward = res.reward;
  return res ? res.reward : null;
}

/** Undo for the light celebration (0.2.0): the card just finished is to do again, its points come off and the 'done'
 *  note hides. Only the latest card, today, and not a big one (the server answers 409 otherwise). -> { state } | null. */
export const undoneCard = (id: string) => optimistic((s) => {
  const c = card(s, id); if (!c || c.st !== 'done') return;
  c.st = 'todo'; c.doneAt = null;
  const note = [...s.messages].reverse().find((m) => m.kind === 'done' && (m.data as DoneNoteData)?.cardId === id && !(m.data as DoneNoteData).undone);
  const gained = note ? (note.data as DoneNoteData).gained : s.reward?.id === id ? s.reward.gained : lastReward?.id === id ? lastReward.gained : 0;
  if (note) note.data = { ...note.data, undone: true };
  if (gained) { s.points -= gained; s.level = levelOf(s.points); }
  if (s.reward?.id === id) s.reward = null;
  s.collection.cardsDone = Math.max(0, s.collection.cardsDone - 1);
  s.collection.recent = s.collection.recent.filter((x) => x.id !== id);
}, () => api.cardUndone(id));

export async function removeCard(id: string, opts: { toast?: boolean } = { toast: true }) {
  const res = await optimistic((s) => { const c = card(s, id); if (c) c.st = 'removed'; }, () => api.cardRemove(id));
  if (res && opts.toast !== false) toast('Removed', { undo: () => { dismissToast(); void restoreCard(id); } });
  return res;
}
export const restoreCard = (id: string) => optimistic((s) => { const c = card(s, id); if (c) c.st = 'todo'; }, () => api.cardRestore(id));

/** Resolves with { state, cardId } (cardId = the carry card on tomorrow's list for 'Tomorrow') or null. */
export const laterCard = (id: string, when: LaterWhen) => optimistic((s) => {
  const c = card(s, id); if (!c) return;
  const t = laterTarget(when, localMinutes(Date.parse(s.now), s.user.tz));
  if (t.tomorrow) c.st = 'moved';
  else { c.at = t.at; c.time = fmtTime(t.at); s.plan.cards.sort((a, b) => a.at - b.at || a.order - b.order); }
}, () => api.cardLater(id, when));

export async function stuckCard(id: string, reason: StuckReason): Promise<StuckHelp | null> {
  const res = await optimistic((s) => { const c = card(s, id); if (c) c.stuck = true; }, () => api.cardStuck(id, reason));
  return res ? res.help : null;
}
/** -> { state, cardIds } | null. */
export const splitCard = (id: string, cards: { title: string; min: number }[]) => optimistic(null, () => api.cardSplit(id, cards));
/** -> { state, cardId } | null (the restored card). */
export const unsplitCard = (id: string) => optimistic(null, () => api.cardUnsplit(id));
export const undoChange = (changeId: string) => optimistic((s) => {
  const m = s.messages.find((x) => x.kind === 'change' && x.data?.changeId === changeId);
  if (m) m.data = { ...m.data, undone: true };
}, () => api.changeUndo(changeId));
/** -> { state, cardId } | null (the new card). */
export const acceptNudge = (messageId: string) => optimistic(null, () => api.nudgeAccept(messageId));
/** -> { state, cardId } | null. Accept makes a 2-minute card that is already started: open W with cur = cardId. */
export const answerOffer = (messageId: string, accept: boolean) => optimistic(null, () => api.offer(messageId, accept));
/** Notification button pressed (snooze / not-today / accept) -> { state, cardId? } | null. */
export const notifyAction = (noteId: string, action: NotifyAction) => optimistic(null, () => api.notifyAction(noteId, action));
export const goodnight = () => optimistic(null, () => api.goodnight());
/** "Wrap up the day" from Today: the server writes the evening summary now (once), so it survives a reload. */
export const wrapDay = () => optimistic(null, () => api.wrap());
export const rewardSeen = () => optimistic((s) => { s.reward = null; }, () => api.rewardSeen());

// ------------------------------------------------------------------ stuck in chat (0.2.0)
// The conversation is server-written chat messages (kind 'stuck', data StuckChatData); these show it at once and the
// server's messages replace them. Copy: @shared/content/stuck.
let tmpSeq = 0;
/** A message shown until the server's copy lands. Its time is the server's clock (AppState.now, which debug time
 *  travel moves), never before the last message, so no stray day divider shows in between. */
const tmpMsg = (s: AppState, role: Message['role'], kind: Message['kind'], text: string, data?: StuckChatData): Message => {
  const last = s.messages.length ? Date.parse(s.messages[s.messages.length - 1].ts) : 0;
  const ts = new Date(Math.max(Date.parse(s.now) || Date.now(), last || 0)).toISOString();
  return { id: `tmp_${++tmpSeq}`, ts, role, ch: s.ch, kind, text, ...(data ? { data } : {}) };
};
const stuckData = (m: Message) => m.data as StuckChatData;
const isOpenStuck = (m: Message) => m.kind === 'stuck' && stuckData(m)?.status === 'open';
function pushAsk(s: AppState, c: Card, said: string): Message {
  const you = tmpMsg(s, 'user', 'text', said);
  const ask = tmpMsg(s, 'buddy', 'stuck', STUCK_CHAT[s.ch].ask, { cardId: c.id, title: c.title, step: 'ask', reason: null, status: 'open', said: you.id });
  s.messages.push(you, ask);
  return ask;
}

/** Stuck on a card: no reason = "I'm stuck on …" and her ask with the six reasons; a reason = "{reason}." and her help
 *  (data.help). -> { state, messageId, help? } | null (messageId = her ask, or her help). */
export const stuckChat = (id: string, reason?: StuckReason | null) => optimistic((s) => {
  const c = card(s, id); if (!c) return;
  const open = s.messages.filter(isOpenStuck);
  let ask = open.find((m) => stuckData(m).cardId === id && stuckData(m).step === 'ask') || null;
  if (!reason && ask && s.messages[s.messages.length - 1] === ask) return; // a second tap: the same ask
  if (!reason) ask = null;
  for (const m of open) if (m !== ask) m.data = { ...m.data, status: 'closed' };
  if (!ask) ask = pushAsk(s, c, fill(STUCK_SAID.stuck, { title: c.title }));
  if (reason) {
    ask.data = { ...ask.data, reason, status: 'resolved' };
    c.stuck = true;
    s.messages.push(tmpMsg(s, 'user', 'text', fill(STUCK_SAID.reason, { label: STUCK_REASON_LABEL[reason] })));
  }
}, () => api.cardStuckChat(id, reason || undefined));

/** Act on a stuck message: 'start' (then open W on the returned cardId), 'later' { when } (a time chip), 'split' (the
 *  small cards), 'again' ("Something else": she asks again), 'close' ("Never mind").
 *  -> { state, cardId?, cardIds?, messageId? } | null. */
export const stuckAction = (messageId: string, a: StuckAction) => optimistic((s) => {
  const m = s.messages.find((x) => x.id === messageId && x.kind === 'stuck'); if (!m) return;
  const d = stuckData(m);
  if (a.action === 'close') { if (d.status === 'open') m.data = { ...d, status: 'closed' }; return; }
  const c = card(s, d.cardId);
  if (d.status !== 'open' || !c || c.st !== 'todo') return;
  if (a.action === 'again') { m.data = { ...d, status: 'closed' }; pushAsk(s, c, STUCK_SAID.again); return; }
  if (!d.help) return;
  if (a.action === 'start') {
    c.startedAt = c.startedAt || nowIso(); c.alt = stuckAlt(d.help, d.title);
    m.data = { ...d, status: 'resolved', act: 'start', out: stuckOutStart(s.ch, d.help, d.title) };
  } else if (a.action === 'later') {
    const t = laterTarget(a.when, localMinutes(Date.parse(s.now), s.user.tz));
    if (t.tomorrow) c.st = 'moved';
    else { c.at = t.at; c.time = fmtTime(t.at); s.plan.cards.sort((x, y) => x.at - y.at || x.order - y.order); }
    m.data = { ...d, status: 'resolved', act: 'later', out: stuckOutLater(d.title, a.when) };
  }
  // 'split': the server makes the small cards.
}, () => api.stuckAction(messageId, a));

// ------------------------------------------------------------------ us + me
/** U3 "What got easier?" -> { reply, text } (text = the transcript when audio was sent) or null. */
export async function weekAnswer(body: { text?: string; audio?: AudioPayload }): Promise<{ reply: string; text: string } | null> {
  try { const r = await api.weekAnswer(body); applyState(r.state); return { reply: r.reply, text: r.text }; } catch (e) { toast(errorText(e)); return null; }
}
export async function weekChest(): Promise<number | null> {
  try { const r = await api.weekChest(); applyState(r.state); return r.points; } catch (e) { toast(errorText(e)); return null; }
}
export const storyChoice = (chap: number, choice: string) => optimistic((s) => { s.buddy.chapterChoices[String(chap)] = choice; }, () => api.storyChoice(chap, choice));
/** Chapter opened: clears the "new chapter" dot. */
export const storyRead = (chap: number) => optimistic((s) => { if (s.buddy.newChapter === chap) s.buddy.newChapter = null; }, () => api.storyRead(chap));
/** Older chat history (scroll up in C): the messages before `beforeId`, oldest first. Not merged into state. */
export async function olderMessages(beforeId: string, limit = 60) {
  try { return (await api.messages(beforeId, limit)).messages; } catch (e) { toast(errorText(e)); return null; }
}
export const editMemory = (id: string, text: string) => optimistic((s) => { s.buddy.memory = s.buddy.memory.map((m) => (m.id === id ? { ...m, text } : m)); }, () => api.memoryEdit(id, text));
export const deleteMemory = (id: string) => optimistic((s) => { s.buddy.memory = s.buddy.memory.filter((m) => m.id !== id); }, () => api.memoryDelete(id));
export const forgetAll = () => optimistic((s) => { s.buddy.memory = []; }, () => api.memoryForgetAll());
export const updateSettings = (patch: SettingsPatch) => optimistic((s) => {
  if (patch.planTime) { s.user.planTime = patch.planTime; s.tomorrowPlanTime = patch.planTime; }
  if (patch.checkin) s.user.checkin = patch.checkin;
  if (typeof patch.reminders === 'boolean') s.user.reminders = patch.reminders;
  if (patch.tz === 'auto') s.user.tzAuto = true;
  else if (patch.tz) { s.user.tzAuto = false; s.user.tz = patch.tz; }
}, () => api.settings(patch));
export const switchBuddy = (ch: CharId) => optimistic(null, () => api.buddySwitch(ch));
export const setVip = (on: boolean) => optimistic((s) => { s.user.vip = on; }, () => api.vip(on));
export const friendAction = (id: string, action: FriendAction) => optimistic((s) => { s.friends.sent[id] = action; }, () => api.friend(id, action));
export const shareWithFriends = () => optimistic(null, () => api.friendsShare());
/** Test tools (M1 in the internal build). 'restart' signs out (the account is deleted): see restartAccount(). */
export async function debugTool(tool: DebugTool, body: DebugBody = {}) {
  if (tool === 'restart') { await restartAccount(); return null; }
  return optimistic(null, () => api.debug(tool, body));
}

// ------------------------------------------------------------------ chat
let chatAbort: AbortController | null = null;
/** How a send ended: 'ok' (the stream finished; an `error` event may still have stopped the turn, see onEvent),
 *  'failed' (network or server failure), 'aborted' (a newer send replaced it). */
export type ChatOutcome = 'ok' | 'failed' | 'aborted';
/** Send a chat message (text, voice or photo). Streams into appStore.chat; the final state replaces messages.
 *  `quiet`: errors before the user's message lands are the caller's to show (no toast, no chat.error); the voice
 *  bubble says "Didn't go through" itself. Errors after that are shown as usual. */
export async function sendChat(body: ChatRequest, onEvent?: (e: ChatEvent) => void, opts: { quiet?: boolean } = {}): Promise<ChatOutcome> {
  chatAbort?.abort();
  const ctl = new AbortController();
  chatAbort = ctl;
  const setChat = (p: Partial<ChatStream>) => appStore.set((s) => ({ chat: { ...s.chat, ...p } }));
  setChat({ sending: true, pending: [], text: '', error: null, rewardFor: null });
  const rewardBefore = appStore.get().state?.reward?.id || null;
  let landed = false;
  const loud = () => !opts.quiet || landed;
  try {
    const final = await api.chat(body, (e) => {
      if (e.t === 'user' || e.t === 'limit' || e.t === 'crisis') landed = true;
      if (e.t === 'user' || e.t === 'message') setChat({ pending: appStore.get().chat.pending.concat([e.message]), text: e.t === 'message' ? '' : appStore.get().chat.text });
      else if (e.t === 'delta') setChat({ text: appStore.get().chat.text + e.text });
      else if (e.t === 'limit') setChat({ limit: true });
      else if (e.t === 'crisis') setChat({ crisis: true });
      else if (e.t === 'error') { if (loud()) setChat({ error: e.message }); }
      else if (e.t === 'end') applyState(e.state);
      onEvent?.(e);
    }, ctl.signal);
    if (final) applyState(final);
    const rw = appStore.get().state?.reward || null;
    setChat({ sending: false, pending: [], text: '', rewardFor: rw && rw.id !== rewardBefore ? rw.id : null });
    return 'ok';
  } catch (e) {
    if (e instanceof ApiError && e.code === 'aborted') return 'aborted';
    setChat({ sending: false, error: loud() ? errorText(e) : null });
    if (loud()) toast(errorText(e));
    return 'failed';
  } finally {
    if (chatAbort === ctl) chatAbort = null;
  }
}
export const sendPhoto = (image: ImagePayload, text?: string) => sendChat({ image, text });
export function clearChatFlags() { appStore.set((s) => ({ chat: { ...s.chat, limit: false, crisis: false, rewardFor: null, error: null } })); }
